// Dependencies
import styled, { css } from 'styled-components';

// Types
import { ContainerProps } from './types';
import { Colors } from '../../../styles/theme/types';

// Components
import { CloseIcon } from '../../../assets/icons/CloseIcon';
import { PlusIcon } from '../../../assets/icons/PlusIcon';
import { VisibilityIcon } from '../../../assets/icons/VisibilityIcon';
import { ArrowBottom } from '../../../assets/icons/ArrowBottom';
import { Typography } from '../Typography';
import { hoverCapable } from '../../../styles/hover';

export const Container = styled.div<ContainerProps>`
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-radius: 100rem;
  width: max-content;
  ${({ variant, theme }) =>
    ({
      campaign: css`
        background-color: ${theme.colors.pink};
      `,
      open: css`
        background-color: ${theme.colors.blue};
      `,
      managed: css`
        background-color: ${theme.colors.secondaryOrange};
      `,
      'pending-work': css`
        background-color: ${theme.colors.gold};
      `,
      'work-in-progress': css`
        background-color: ${theme.colors.blueSoft};
      `,
      // Color PROPIO desde 2026-08-05: `in-progress` (RV) copiaba el
      // `blueSoft` de `work-in-progress` y las dos pills eran indistinguibles
      // pese a ser estados distintos. Misma familia azul, más intenso
      // ("más intenso = cronómetro corriendo"), y el contraste con el texto
      // blanco sube de 1,91:1 a 4,66:1.
      'in-progress': css`
        background-color: ${theme.colors.blueRouteInProgress};
      `,
      paused: css`
        background-color: ${theme.colors.gold};
      `,
      'pending-validation': css`
        background-color: ${theme.colors.purple};
      `,
      'pending-at-validation': css`
        background-color: ${theme.colors.blueOT};
      `,
      validated: css`
        background-color: ${theme.colors.green};
      `,
      'pending-certification': css`
        background-color: ${theme.colors.tagSurfaceTeal};
      `,
      partial: css`
        background-color: ${theme.colors.gold};
      `,
      end: css`
        background-color: ${theme.colors.greyBoard};
      `,
      annulled: css`
        background-color: ${theme.colors.tagSurfaceRed};
      `,
      surveillanceRoute: css`
        background-color: ${theme.colors.pink};
      `,
      duplicate: css`
        background-color: ${theme.colors.garnet};
      `,
      roid: css`
        background-color: ${theme.colors.garnet};
        border: none;
      `,
      default: css`
        background-color: ${theme.colors.garnet};
        border: none;
      `,
      transfer: css`
        background-color: ${theme.colors.garnet};
        border: none;
      `,
      transferred: css`
        background-color: ${theme.colors.forestGreen};
        border: none;
      `,
      // P15 Task 1 (2026-07-09) — cross-links RV, ver `types.ts` para el
      // razonamiento semántico de cada color.
      linkedToSegment: css`
        background-color: ${theme.colors.forestGreen};
        border: none;
      `,
      linkedFromSegment: css`
        background-color: ${theme.colors.forestGreen};
        border: none;
      `,
      pendingLink: css`
        background-color: ${theme.colors.secondaryOrange};
        border: none;
      `,
      // Mismo ámbar, texto blanco (lo pone el <Text> por defecto).
      unlinkedWork: css`
        background-color: ${theme.colors.secondaryOrange};
        border: none;
      `,
      linkCta: css`
        background-color: ${theme.colors.garnet};
        border: none;
      `,
      // Chip clicable que lleva al recorrido: mismo tratamiento que los otros
      // chips-que-navegan de la app (garnet + texto blanco).
      fromRoute: css`
        background-color: ${theme.colors.garnet};
        border: none;
      `,
    })[variant]}
  ${({ sizeTag, theme }) =>
    ({
      default: css`
        height: 3rem;
        padding: 0 2rem;
      `,
      medium: css`
        height: 2.5rem;
        padding: 0 1.1rem;
      `,
      small: css`
        height: 1.8rem;
        padding: 0 ${theme.spacing.s};
      `,
      circle: css`
        height: 0.8rem;
        width: 0.8rem;
        border-radius: 100;
      `,
    })[sizeTag]}
`;

export const Title = styled.span`
  color: ${({ theme }) => theme.colors.black};
`;

export const ImageIcon = styled(CloseIcon)`
  width: 1.1rem;
  height: 1.1rem;
  path {
    fill: ${({ theme }) => theme.colors.white};
  }
`;

export const Text = styled(Typography)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

// P15 — slot `actions` del SpecialTag: hoy hospeda el caret del
// TagActionsMenu (antes, los mini-iconos inline de 16px de TagActionIcon,
// retirados en la opción C por ser demasiado pequeños para tablet).
export const ActionsContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: 0.6rem;
  flex-shrink: 0;
`;

// ═══════════════════════════════════════════════════════════════════
// TagActionsMenu (P15 opción C, 2026-07-10) — los mini-iconos de 16px
// dentro de la pill eran demasiado pequeños para tablet. La pill (target
// grande, sin crecer respecto a sus vecinas) abre un menú anclado con
// filas TÁCTILES de 4.4rem. El panel replica los tokens del popover
// canónico de la app (`DropDownUserMenu.Container`: sombra dropdown,
// radius lg, fondo blanco, z-index alto).
// ═══════════════════════════════════════════════════════════════════

export const MenuAnchor = styled.span`
  position: relative;
  display: inline-flex;
`;

// Caret dentro del slot `actions` de la pill — indica que despliega menú
// sin alterar la altura del tag (mismo hueco que ocupaban los mini-iconos).
export const MenuCaret = styled(ArrowBottom)<{
  $open?: boolean;
  /** Mismo color que la letra de su etiqueta (`tagTextColor`). */
  $fill?: keyof Colors;
}>`
  width: 1rem;
  height: 0.8rem;
  flex-shrink: 0;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);

  path {
    fill: ${({ theme, $fill }) => theme.colors[$fill ?? 'white']};
  }
`;

// Panel con los tokens del dropdown canónico (`DropDownList.List`): blanco,
// sombra dropdown, radio 0.5rem, texto greyVeryDark. `overflow: hidden` para
// que el hover lila de las filas llegue de borde a borde respetando el radio.
export const MenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 0.6rem);
  right: 0;
  display: flex;
  flex-direction: column;
  min-width: 24rem;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.greyVeryDark};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 0.5rem;
  box-shadow: ${({ theme }) => theme.shadows.dropdown};
  z-index: 999999;
`;

// Fila de acción: min-height 4.4rem = target táctil mínimo (Apple HIG /
// Material ~44-48px), el motivo de todo este menú. Hover = firma de los
// dropdowns ROIS (`DropDownList.ListItem`): backgroundLila + medium.
export const MenuRow = styled.button`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  width: 100%;
  min-height: 4.4rem;
  padding: 0.6rem 1.6rem;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.S};
  color: ${({ theme }) => theme.colors.greyVeryDark};

  /* Táctil: sin el guard el resaltado se queda pegado tras el toque. */
  ${hoverCapable} {
    &:hover {
      background-color: ${({ theme }) => theme.colors.backgroundLila};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
    }
  }

  &:focus-visible {
    background-color: ${({ theme }) => theme.colors.backgroundLila};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    outline: none;
  }
`;

// Icono de la fila = mini `ButtonIconBackground` (el círculo garnet con
// icono blanco de las barras de filtros — el tratamiento de iconos de
// acción reconocible de la app), a 2.8rem para la densidad del menú. Los
// SVG (VisibilityIcon / pencil.svg) ya son blancos: sobre garnet no
// necesitan tinte.
export const MenuRowIconCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  height: 2.8rem;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme }) => theme.colors.garnet};
`;

export const MenuRowVisibilityIcon = styled(VisibilityIcon)`
  width: 1.4rem;
  height: 0.96rem;
  flex-shrink: 0;
`;

export const MenuRowEditIcon = styled.img`
  width: 1.2rem;
  height: 1.2rem;
  flex-shrink: 0;
`;

// Vincular (spec § 16): «+» de trazo grueso en blanco, del mismo peso que el
// ojo, el lápiz y la ✕ de las otras filas (el add-icon.svg era de línea fina).
export const MenuRowLinkIcon = styled(PlusIcon)`
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;

  path {
    stroke: ${({ theme }) => theme.colors.white};
  }
`;

// Desvincular (opción A, 2026-07-10): la ✕ canónica (CloseIcon) en blanco
// sobre el mismo círculo garnet que ver/editar.
export const MenuRowUnlinkIcon = styled(CloseIcon)`
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;

  path {
    fill: ${({ theme }) => theme.colors.white};
  }
`;
